import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DataGrid } from "@mui/x-data-grid";
import {
  fetchCareerAtRahejaList,
  editCareerAtRaheja,
  updateCareerAtRaheja,
  trashCareerAtRaheja,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";

const ListCareerAtRaheja = () => {
  const [careerData, setCareerData] = useState([]); // List of career data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editData, setEditData] = useState(null); // State for the item being edited
  const [saving, setSaving] = useState(false); // State for saving edits
  const [deleteDataId, setDeleteDataId] = useState(null); // State for deleting an item
  const [newImage, setNewImage] = useState(null); // State for new image upload

  // Fetch Career Data List
  useEffect(() => {
    const loadCareerData = async () => {
      try {
        const response = await fetchCareerAtRahejaList();
        if (response?.data) {
          setCareerData(response.data); // Set the fetched data
        } else {
          setError("Failed to fetch Career at Raheja data.");
        }
      } catch (err) {
        setError("An error occurred while fetching Career at Raheja data.");
      } finally {
        setLoading(false);
      }
    };

    loadCareerData();
  }, []);

  // Handle Edit Click
  const handleEdit = async (id) => {
    try {
      const response = await editCareerAtRaheja(id);
      if (response?.data?.length > 0) {
        const item = response.data[0];
        setEditData({
          id: item.id,
          title: item.title,
          heading: item.heading,
          sub_heading: item.sub_heading,
          content: item.content,
          img: item.img,
        });
        setNewImage(null);
      } else {
        setError("No data found for editing.");
      }
    } catch (err) {
      console.error("Error loading edit data:", err);
      setError("An error occurred while fetching edit data.");
    }
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    setSaving(true);

    const formData = new FormData();
    formData.append("title", editData.title);
    formData.append("heading", editData.heading);
    formData.append("sub_heading", editData.sub_heading);
    formData.append("content", editData.content);
    if (newImage) formData.append("img", newImage);

    try {
      const response = await updateCareerAtRaheja(editData.id, formData);
      if (response?.message) {
        // Replace the updated data in the list
        setCareerData((prevData) =>
          prevData.map((item) =>
            item.id === editData.id ? { ...item, ...editData } : item
          )
        );
        setEditData(null); // Close the dialog
        setError(null); // Reset error
        toast.success("Career entry updated successfully!");
      } else {
        setError("Failed to save changes.");
      }
    } catch (err) {
      console.error("Error saving edit:", err);
      setError("An error occurred while saving changes.");
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    try {
      await trashCareerAtRaheja(id);
      // Remove the deleted item from the list
      setCareerData((prevData) => prevData.filter((item) => item.id !== id));
      setDeleteDataId(null);
    } catch (err) {
      console.error("Error deleting Career at Raheja data:", err);
    }
  };

  // DataGrid Columns
  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "title", headerName: "Title", width: 200 },
    { field: "heading", headerName: "Heading", width: 200 },
    { field: "sub_heading", headerName: "Sub Heading", width: 200 },
    { field: "content", headerName: "Content", width: 300 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)} // Open the edit dialog
          >
            Edit
          </Button>
          {/* <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteDataId(params.row.id)} // Open delete confirmation
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button> */}
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Typography variant="h4" gutterBottom>
        Career at Raheja List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid rows={careerData} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      <ToastContainer position="top-right" />
      {editData && (
        <Dialog open onClose={() => setEditData(null)}>
          <DialogTitle>Edit Career at Raheja</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              value={editData.title || ""}
              onChange={(e) =>
                setEditData({ ...editData, title: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Heading"
              value={editData.heading || ""}
              onChange={(e) =>
                setEditData({ ...editData, heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Sub Heading"
              value={editData.sub_heading || ""}
              onChange={(e) =>
                setEditData({ ...editData, sub_heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Content"
              multiline
              rows={4}
              value={editData.content || ""}
              onChange={(e) =>
                setEditData({ ...editData, content: e.target.value })
              }
            />
            {/* Image Upload */}
            <Box mt={2}>
              <Button variant="contained" component="label">
                Upload New Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => setNewImage(e.target.files[0])}
                />
              </Button>

              {/* Show uploaded image name or existing image preview */}
              {newImage ? (
                <Typography mt={1}>{newImage.name}</Typography>
              ) : editData.img ? (
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Existing Image:
                  </Typography>
                  <img
                    src={`${baseImage_path}${editData.img}`}
                    alt="Current"
                    style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
                    onError={(e) => {
                      e.target.onerror = null; // Prevent infinite loop if fallback fails
                      e.target.style.display = "none"; // Hide broken image
                      e.target.nextSibling.style.display = "block"; // Show fallback filename
                    }}
                  />
                  {/* Hidden by default, shown if image load fails */}
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ display: "none" }}
                  >
                    {editData.img.split("/").pop()}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditData(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      {deleteDataId && (
        <Dialog open onClose={() => setDeleteDataId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this entry?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDataId(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteDataId)}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ListCareerAtRaheja;
